import Table from "@common/Table";
import { cellRenderer, headerCellRenderer } from "@utils/helpers";
import { useUpdateImportsAndExportsMutation } from "@store/master";
import { Popover } from "antd";
import dayjs from "dayjs";
import { useCallback, useMemo, useRef, useState } from "react";
import EditCellPopover from "./EditCellPopover";

const PAGE_SIZE_OPTIONS = ["10", "30", "50", "100", "200", "500"];

const EDITABLE_FIELDS = [
  "Country (Manufacturer)",
  "Manufacturer",
  "Consignee",
  "Country (Consignee)",
];

const FIELD_TO_UPDATE_MAP = {
  "Country (Manufacturer)": "exportingCountry",
  "Country (Consignee)": "importingCountry",
  Consignee: "importer",
  Manufacturer: "exporter",
};

const READ_ONLY_COLUMNS = [
  {
    title: headerCellRenderer("Date", "!text-xs"),
    dataIndex: "Date",
    key: "Date",
    render: (text) => cellRenderer(text),
  },
  {
    title: headerCellRenderer("Product", "!text-xs"),
    dataIndex: "Product",
    key: "Product",
    render: (text) => cellRenderer(text),
  },
];

export default function TableArea({
  tableData,
  loading,
  pagination,
  onPaginationChange,
  onCellUpdated,
}) {
  const tableWrapperRef = useRef(null);
  const [activeEdit, setActiveEdit] = useState(null);

  const [updateImportAndExport, { isLoading: isUpdating }] =
    useUpdateImportsAndExportsMutation();

  const dataSource = useMemo(
    () =>
      tableData?.map((item) => ({
        key: item.id,
        Date: item.date ? dayjs(item.date).format("DD MMM YYYY") : null,
        Product: item.Product?.name,
        "Country (Manufacturer)": item.LeavesCountry?.name,
        Manufacturer: item.Exporter?.CompanyMaster?.name,
        Consignee: item.Importer?.CompanyMaster?.name,
        "Country (Consignee)": item.ArrivalCountry?.name,
      })) ?? [],
    [tableData],
  );

  const handleCloseEdit = useCallback(() => {
    setActiveEdit(null);
  }, []);

  const handleSaveEdit = useCallback(
    async (newValue) => {
      if (!activeEdit) return;

      const { recordKey, field, value } = activeEdit;
      const trimmed = newValue.trim();
      const initialTrimmed = (value ?? "").trim();

      if (trimmed === initialTrimmed) {
        setActiveEdit(null);
        return;
      }

      try {
        const result = await updateImportAndExport({
          id: recordKey,
          fieldToUpdate: FIELD_TO_UPDATE_MAP[field],
          updateWithValue: trimmed,
        }).unwrap();

        if (result?.success) {
          setActiveEdit(null);
          onCellUpdated?.(recordKey, field, trimmed);
        }
      } catch {
        // Error toast handled in mutation onQueryStarted
      }
    },
    [activeEdit, updateImportAndExport, onCellUpdated],
  );

  const renderEditableCell = useCallback(
    (field) => (text, record) => {
      const isOpen =
        activeEdit?.recordKey === record.key && activeEdit?.field === field;
      const isDisabled = loading || isUpdating;

      return (
        <Popover
          open={isOpen}
          onOpenChange={(open) => {
            if (!open) {
              handleCloseEdit();
            }
          }}
          trigger="click"
          placement="bottomLeft"
          destroyTooltipOnHide
          getPopupContainer={() =>
            tableWrapperRef.current ?? document.body
          }
          content={
            <EditCellPopover
              open={isOpen}
              initialValue={activeEdit?.value ?? text ?? ""}
              fieldLabel={field}
              isSaving={isUpdating}
              onCancel={handleCloseEdit}
              onSave={handleSaveEdit}
            />
          }
        >
          <div
            className={`${
              isDisabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:underline"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (isDisabled) return;
              setActiveEdit({
                recordKey: record.key,
                field,
                value: text ?? "",
              });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                if (isDisabled) return;
                setActiveEdit({
                  recordKey: record.key,
                  field,
                  value: text ?? "",
                });
              }
            }}
            role="button"
            tabIndex={isDisabled ? -1 : 0}
            aria-label={`Edit ${field}`}
          >
            {cellRenderer(text)}
          </div>
        </Popover>
      );
    },
    [
      activeEdit,
      loading,
      isUpdating,
      handleCloseEdit,
      handleSaveEdit,
    ],
  );

  const columns = useMemo(
    () => [
      ...READ_ONLY_COLUMNS,
      ...EDITABLE_FIELDS.map((field) => ({
        title: headerCellRenderer(field, "!text-xs"),
        dataIndex: field,
        key: field,
        render: renderEditableCell(field),
      })),
    ],
    [renderEditableCell],
  );

  const handleTableChange = (newPagination) => {
    onPaginationChange?.(newPagination);
  };

  return (
    <div
      ref={tableWrapperRef}
      className="w-full md:w-[70%] lg:w-[80%] xl:w-[85%] animate-fadeIn flex flex-col h-[calc(100vh-180px)] min-h-0 [&_.ant-table-body]:custom-scrollbar [&_.ant-table-pagination]:!sticky [&_.ant-table-pagination]:!bottom-0 [&_.ant-table-pagination]:!z-10 [&_.ant-table-pagination]:!bg-secondary-main [&_.ant-table-pagination]:!py-2 [&_.ant-table-pagination]:!mb-0"
    >
      <Table
        columns={columns}
        dataSource={dataSource}
        loading={loading}
        emptyText="No data to show. Adjust search criteria"
        scrollConfig={{ x: 900, y: "calc(100vh - 280px)" }}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
        onChange={handleTableChange}
      />
    </div>
  );
}
